import type { SketchGroup } from '../../../mesh/sketch/SketchGroup';
import {resolveTopologySourceMemberScopeKey} from '../model/ExternalGeometrySourceDescriptor';
import {TopologyOwnerRuntimeHostFactory} from '../source/topology/TopologyOwnerRuntimeHostFactory';
import {TopologyEdgeRelocationService} from '../source/topology/TopologyEdgeRelocationService';
import {TopologyEdgeSourceDescriptorFactory} from '../source/topology/TopologyEdgeSourceDescriptorFactory';
import {TopologySelectionRuntimeSourceSnapshotFactory} from '../source/topology/TopologySelectionRuntimeSourceSnapshotFactory';
import type {TopologyOwnerRuntimeHost} from '../../../../types/reference/sketch-entity-reference/TopologyOwnerRuntimeResolver';
import type { SketchPointObject } from '../../../mesh/sketch/SketchPointObject';
import type {Object3D} from 'three';
import {isAbstractSketchEdgeObject} from '../../../mesh/guards';
import {
	isRetainedSketchEntityReferenceRecord,
	isSketchSourceRebuildableReferenceRecord,
	resolveSourceRefEntityId,
	type SketchEntityReferenceRecord,
} from '../model/SketchEntityReferenceRecord';
import type { RebuildSketchEntityReferencesResult } from '../model/SketchEntityReferenceResults';
import type {
	SketchEntityReferencePropagationContext,
	SketchEntityReferenceTargetChange,
} from '../model/SketchEntityReferencePropagation';
import { createExecutionDiagnostic, type ExecutionDiagnostic } from '../model/SketchEntityReferenceDiagnostics';
import { SketchReferenceTargetAnchorBindingCollector } from '../relation/SketchReferenceTargetAnchorBindingCollector';
import { SketchBasicConstraintRestoreService } from '../relation/SketchBasicConstraintRestoreService';
import { SketchReferenceTargetEndpointCanonicalizer } from '../target/SketchReferenceTargetEndpointCanonicalizer';
import type {SketchEntityReferenceRepositoryWriteScope} from '../repository/SketchEntityReferenceRepository';
import {
	SketchEntityReferenceRebuildBatchTransaction,
	STALE_SKETCH_ENTITY_REFERENCE_REBUILD,
} from './SketchEntityReferenceRebuildBatchTransaction';
import { SketchEntityReferenceTargetChangeCommitter } from './SketchEntityReferenceTargetChangeCommitter';
import {SketchEntityReferenceRebuildBatchPlanner} from './SketchEntityReferenceRebuildBatchPlanner';
import type {SketchEntityReferenceRebuildBatchPlan} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildBatchPlanner';
import type {
	RebuildSketchEntityReferencesBySourceEntitiesInput,
	RebuildSketchEntityReferencesByTopologyOwnersInput,
	RebuildSketchEntityReferencesServiceDependencies,
} from '../../../../types/reference/sketch-entity-reference/RebuildSketchEntityReferencesService';
import type {
	RebuildReferenceRuntimeItem,
	RebuildSingleReferenceResult,
	SketchEntityReferenceRebuildExecutorPort,
	SketchEntityReferenceRebuildExecutorInput,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildExecutor';

export type {
	RebuildSketchEntityReferencesBySourceEntitiesInput,
	RebuildSketchEntityReferencesByTopologyOwnersInput,
	RebuildSketchEntityReferencesServiceDependencies,
	SketchEntityReferenceTopologyOwnerRebuildTrigger,
} from '../../../../types/reference/sketch-entity-reference/RebuildSketchEntityReferencesService';
export type {
	RebuildReferenceRuntimeItem,
	RebuildSketchEntityReferencesRuntime,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceRebuildExecutor';

/**
 * 转换实体引用批次重建服务。
 *
 * 说明：
 * 1. 负责查询引用、组织批次、恢复批次约束并提交下游传播；
 * 2. 单条记录的重新投影、几何修改和失败回滚由 RebuildExecutor 完成；
 * 3. Coordinator 只管理事件与串行队列，不接触重建实现细节。
 */
export class RebuildSketchEntityReferencesService {
	private readonly rebuildExecutor: SketchEntityReferenceRebuildExecutorPort;
	private readonly basicConstraintRestoreService: SketchBasicConstraintRestoreService;
	private readonly targetAnchorBindingCollector: SketchReferenceTargetAnchorBindingCollector;
	private readonly targetChangeCommitter: SketchEntityReferenceTargetChangeCommitter;
	private readonly batchPlanner = new SketchEntityReferenceRebuildBatchPlanner();
	private readonly targetEndpointCanonicalizer = new SketchReferenceTargetEndpointCanonicalizer();
	/** 复用既有几何适配器，避免在同一重建中逐边分配无状态服务。 */
	private readonly topologyHostFactory = new TopologyOwnerRuntimeHostFactory();
	private readonly topologyRelocationService = new TopologyEdgeRelocationService();
	private readonly topologyDescriptorFactory = new TopologyEdgeSourceDescriptorFactory();
	private readonly topologySnapshotFactory = new TopologySelectionRuntimeSourceSnapshotFactory();

	/**
	 * @param dependencies 批次重建依赖。
	 */
	public constructor(private readonly dependencies: RebuildSketchEntityReferencesServiceDependencies) {
		this.rebuildExecutor = dependencies.rebuildExecutor;
		this.basicConstraintRestoreService = dependencies.basicConstraintRestoreService ?? new SketchBasicConstraintRestoreService();
		this.targetAnchorBindingCollector = dependencies.targetAnchorBindingCollector ?? new SketchReferenceTargetAnchorBindingCollector();
		this.targetChangeCommitter = dependencies.targetChangeCommitter ?? new SketchEntityReferenceTargetChangeCommitter();
	}

	/**
	 * 按来源草图执行引用重建。
	 *
	 * @param sourceSketchId 来源草图 id。
	 * @returns 聚合后的重建结果。
	 */
	public async rebuildBySourceSketchId(sourceSketchId: string): Promise<RebuildSketchEntityReferencesResult> {
		return this.executeRebuildTransaction(
			async (repository, transaction) => this.rebuildReferenceBatch(
				(
					await repository.listReferencesBySourceSketch(sourceSketchId)
				).filter(isSketchSourceRebuildableReferenceRecord),
				undefined,
				'keep',
				undefined,
				[],
				() => true,
				[],
				repository,
				transaction,
			),
		);
	}

	/**
	 * 按来源实体集合执行下游引用重建。
	 *
	 * @param input 来源实体重建输入。
	 * @returns 聚合后的重建结果。
	 */
	public async rebuildBySourceEntities(input: RebuildSketchEntityReferencesBySourceEntitiesInput): Promise<RebuildSketchEntityReferencesResult> {
		return this.executeRebuildTransaction(
			async (repository, transaction) => {
				const visitedReferenceIds = new Set(input.visitedReferenceIds ?? []);
				const referenceMap = new Map<string, SketchEntityReferenceRecord>();
				for (const sourceEntityId of input.sourceEntityIds) {
					const references = await repository.listReferencesBySourceEntity(
						input.sourceSketchId,
						sourceEntityId,
					);
					references.forEach(reference => {
						if (!isSketchSourceRebuildableReferenceRecord(reference)
							|| visitedReferenceIds.has(reference.referenceId)) {
							return;
						}
						referenceMap.set(reference.referenceId, reference);
					});
				}
				return this.rebuildReferenceBatch(
					[...referenceMap.values()],
				{
					propagationId: input.propagationId,
					visitedReferenceIds: [...visitedReferenceIds],
				},
				'keep',
				undefined,
				[],
					() => true,
					[],
					repository,
					transaction,
				);
			},
		);
	}

	/**
	 * 按 topology owner 集合重建转换实体引用。
	 *
	 * @param input topology owner 批量重建输入。
	 * @returns 聚合后的重建结果。
	 */
	public async rebuildByTopologyOwners(input: RebuildSketchEntityReferencesByTopologyOwnersInput): Promise<RebuildSketchEntityReferencesResult> {
		const isInputCurrent = input.isInputCurrent ?? (() => true);
		if (!isInputCurrent()) {
			return this.createEmptyRebuildResult();
		}
		return this.executeRebuildTransaction(
			async (repository, transaction) => {
				const referenceMap = new Map<string, SketchEntityReferenceRecord>();
				for (const owner of input.owners) {
					const references = await repository.listReferencesByTopologyOwner({
						...owner,
						statusScope: 'retained',
					});
					if (!isInputCurrent()) {
						throw STALE_SKETCH_ENTITY_REFERENCE_REBUILD;
					}
					references.forEach(reference => {
						if (isRetainedSketchEntityReferenceRecord(reference)) {
							referenceMap.set(reference.referenceId, reference);
						}
					});
				}
				return this.rebuildReferenceBatch(
					[...referenceMap.values()],
				undefined,
				'keep',
				undefined,
				[],
				isInputCurrent,
				[],
				repository,
					transaction,
				);
			},
			isInputCurrent,
		);
	}

	/**
	 * 使用执行前冻结的引用记录更新当前历史 checkpoint 的正式草图。
	 *
	 * 该入口不重新查询仓库，因此本轮运行期间新增或删除的引用不会改变既定处理集合。
	 * 来源无法从 checkpoint topology 重定位时写入 dangling，并继续处理同批其他引用。
	 *
	 * @param references 执行开始前捕获并按当前 checkpoint 过滤的引用副本。
	 * @param propagationId 当前时间线重建的传播 ID。
	 * @param isInputCurrent 当前 checkpoint 是否仍拥有正式状态提交权。
	 * @returns 正式草图引用更新结果。
	 */
	public async rebuildTimelineCheckpointReferences(
		references: readonly SketchEntityReferenceRecord[],
		propagationId: string,
		isInputCurrent: () => boolean,
	): Promise<RebuildSketchEntityReferencesResult> {
		return this.executeRebuildTransaction(
			(repository, transaction) => this.rebuildTimelineCheckpointCandidates(
					references,
					propagationId,
					isInputCurrent,
					repository,
					transaction,
				),
			isInputCurrent,
		);
	}

	/**
	 * 在唯一仓储工作副本和运行时补偿事务中执行一次引用重建。
	 *
	 * @param work 只接触事务写作用域的候选计算。
	 * @param isInputCurrent 当前异步输入是否仍拥有提交权。
	 * @returns 已提交结果；stale 时返回空结果。
	 */
	private async executeRebuildTransaction(
		work: (
			repository: SketchEntityReferenceRepositoryWriteScope,
			transaction: SketchEntityReferenceRebuildBatchTransaction,
		) => Promise<RebuildSketchEntityReferencesResult>,
		isInputCurrent: () => boolean = () => true,
	): Promise<RebuildSketchEntityReferencesResult> {
		if (!isInputCurrent()) {
			return this.createEmptyRebuildResult();
		}
		const transaction = new SketchEntityReferenceRebuildBatchTransaction();
		let finalizationDiagnostics: ExecutionDiagnostic[] = [];
		try {
			const result = await this.dependencies.repository.withWriteTransaction(
				repository => work(repository, transaction),
				isInputCurrent,
				() => {
					finalizationDiagnostics = transaction.finalize();
				},
			);
			return {
				...result,
				diagnostics: [...result.diagnostics, ...finalizationDiagnostics],
			};
		} catch (error) {
			const rollbackDiagnostics = await transaction.rollbackAll();
			if (error === STALE_SKETCH_ENTITY_REFERENCE_REBUILD || !isInputCurrent()) {
				return {
					...this.createEmptyRebuildResult(),
					diagnostics: rollbackDiagnostics,
				};
			}
			throw error;
		}
	}

	/**
	 * 在 checkpoint 唯一仓储工作副本中完成成员协调和单条引用候选计算。
	 *
	 * @param references 执行开始前冻结的引用记录。
	 * @param propagationId 当前时间线传播 ID。
	 * @param isInputCurrent 当前 checkpoint 所有权校验。
	 * @param repository 唯一仓储事务的工作副本。
	 * @param transaction 运行时补偿事务。
	 * @returns 尚未提交到正式仓储的重建结果。
	 */
	private async rebuildTimelineCheckpointCandidates(
		references: readonly SketchEntityReferenceRecord[],
		propagationId: string,
		isInputCurrent: () => boolean,
		repository: SketchEntityReferenceRepositoryWriteScope,
		transaction: SketchEntityReferenceRebuildBatchTransaction,
	): Promise<RebuildSketchEntityReferencesResult> {
		if (!isInputCurrent()) {
			throw STALE_SKETCH_ENTITY_REFERENCE_REBUILD;
		}
		const targetSketchBaselineDefinitionRevisions = new Map<string, number>();
		const dirtyTargetSketchIdsAtBatchStart = new Set<string>();
		references.forEach((reference) => {
			if (targetSketchBaselineDefinitionRevisions.has(reference.targetSketchId)) {
				return;
			}
			const targetSketch = this.dependencies.runtime.getSketchGroup(reference.targetSketchId);
			if (!targetSketch) {
				return;
			}
			targetSketchBaselineDefinitionRevisions.set(
				reference.targetSketchId,
				targetSketch.definitionRevision,
			);
			if (targetSketch.hasUncommittedDefinitionChanges()) {
				dirtyTargetSketchIdsAtBatchStart.add(reference.targetSketchId);
			}
		});
		const boundaryReconcileResult = this.dependencies.topologyBoundaryGroupReconciler
			? await this.dependencies.topologyBoundaryGroupReconciler.reconcile(
					references,
					propagationId,
					isInputCurrent,
					repository,
					transaction,
				)
			: {
				references: references.map((reference) => structuredClone(reference)),
				createdReferenceIds: [],
				removedReferenceIds: [],
				failedReferenceIds: [],
				replacementReferenceIds: [],
				targetChanges: [],
				diagnostics: [],
			};
		const rebuildResult = await this.rebuildReferenceBatch(
			boundaryReconcileResult.references,
			{
				propagationId,
				visitedReferenceIds: [],
				definitionChangeOwnerId: propagationId,
			},
			'dangling',
			{
				targetSketchBaselineDefinitionRevisions,
				dirtyTargetSketchIdsAtBatchStart,
			},
			boundaryReconcileResult.targetChanges,
			isInputCurrent,
			boundaryReconcileResult.replacementReferenceIds,
			repository,
			transaction,
		);
		const failedReferenceIdSet = new Set([
			...boundaryReconcileResult.failedReferenceIds,
			...rebuildResult.failedReferenceIds,
		]);
		return {
			keptReferenceIds: rebuildResult.keptReferenceIds.filter(
				referenceId => !failedReferenceIdSet.has(referenceId),
			),
			removedReferenceIds: [
				...boundaryReconcileResult.removedReferenceIds,
				...rebuildResult.removedReferenceIds,
			].filter(referenceId => !failedReferenceIdSet.has(referenceId)),
			failedReferenceIds: [...failedReferenceIdSet],
			diagnostics: [
				...boundaryReconcileResult.diagnostics,
				...rebuildResult.diagnostics,
			],
		};
	}

	/**
	 * 按“参与指定草图”的引用执行重建。
	 *
	 * 说明：
	 * 1. 草图缩放 committed 只声明“哪一个草图被整体缩放”；
	 * 2. 该草图可能是引用的 source sketch，也可能是 target sketch；
	 * 3. 因此这里需要合并两侧命中的引用，再统一执行重建。
	 *
	 * @param sketchId 参与引用关系的草图 id。
	 * @returns 聚合后的重建结果。
	 */
	public async rebuildByParticipatingSketchId(sketchId: string): Promise<RebuildSketchEntityReferencesResult> {
		return this.executeRebuildTransaction(
			async (repository, transaction) => {
				const sourceReferences = await repository.listReferencesBySourceSketch(sketchId);
				const targetReferences = await repository.listReferencesByTargetSketch(
					sketchId,
					'retained',
				);
				const referenceMap = new Map<string, SketchEntityReferenceRecord>();
				sourceReferences.forEach(reference => {
					if (isSketchSourceRebuildableReferenceRecord(reference)) {
						referenceMap.set(reference.referenceId, reference);
					}
				});
				targetReferences.forEach(reference => {
					if (isRetainedSketchEntityReferenceRecord(reference)) {
						referenceMap.set(reference.referenceId, reference);
					}
				});
				return this.rebuildReferenceBatch(
					[...referenceMap.values()],
				undefined,
				'keep',
				undefined,
				[],
					() => true,
					[],
					repository,
					transaction,
				);
			},
		);
	}

	/**
	 * 执行一批引用重建。
	 *
	 * @param references 待重建引用列表。
	 * @param propagationContext 下游传播与定义变更归属。
	 * @param sourceMissingPolicy 来源缺失时的处理策略。
	 * @param batchStartState 批次开始时冻结的目标草图状态。
	 * @param additionalTargetChanges 成员协调阶段已经产生的目标变化。
	 * @param isInputCurrent 当前异步任务是否仍属于活动文档。
	 * @param forcedReplacementReferenceIds 共享端点拓扑冲突、必须退出原位复用的引用 ID。
	 * @param repository 当前调用使用的仓储写作用域。
	 * @param transaction 当前调用统一持有的运行时补偿事务。
	 * @returns 聚合后的重建结果。
	 */
	private async rebuildReferenceBatch(
		references: SketchEntityReferenceRecord[],
		propagationContext?: Pick<SketchEntityReferencePropagationContext, 'propagationId' | 'visitedReferenceIds'> & {
			/** 阶段 B checkpoint 内部定义变更归属。 */
			definitionChangeOwnerId?: string;
		},
		sourceMissingPolicy: 'keep' | 'dangling' = 'keep',
		batchStartState?: {
			/** 成员协调前捕获的目标草图定义版本。 */
			targetSketchBaselineDefinitionRevisions: Map<string, number>;
			/** 成员协调前已经存在未提交变更的目标草图。 */
			dirtyTargetSketchIdsAtBatchStart: Set<string>;
		},
		additionalTargetChanges: SketchEntityReferenceTargetChange[] = [],
		isInputCurrent: () => boolean = () => true,
		forcedReplacementReferenceIds: readonly string[] = [],
		repository: SketchEntityReferenceRepositoryWriteScope = this.dependencies.repository,
		transaction?: SketchEntityReferenceRebuildBatchTransaction,
	): Promise<RebuildSketchEntityReferencesResult> {
		if (!isInputCurrent()) {
			if (transaction) {
				throw STALE_SKETCH_ENTITY_REFERENCE_REBUILD;
			}
			return this.createEmptyRebuildResult();
		}
		const keptReferenceIds: string[] = [];
		const removedReferenceIds: string[] = [];
		const failedReferenceIds: string[] = [];
		const forcedReplacementReferenceIdSet = new Set(forcedReplacementReferenceIds);
		const batchPlans: SketchEntityReferenceRebuildBatchPlan[] = transaction
			? this.batchPlanner.plan({
				references,
				endpointEvidenceByReferenceId: this.collectRebuildEndpointEvidence(references),
			})
			: references.map(reference => ({
				targetSketchId: reference.targetSketchId,
				batchKey: reference.referenceId,
				// 非事务兼容路径不会登记运行时 mutation，因此没有需要联动回滚的语义键。
				rollbackKeys: [],
				referenceIds: [reference.referenceId],
			}));
		const batchKeyByReferenceId = new Map<string, string>(
			batchPlans.flatMap(plan => (
				plan.referenceIds.map(referenceId => [referenceId, plan.batchKey] as const)
			)),
		);
		const batchPlanByBatchKey = new Map<string, SketchEntityReferenceRebuildBatchPlan>(
			batchPlans.map(plan => [plan.batchKey, plan] as const),
		);
		const failedBatchKeys = new Set<string>();
		/** 原子批次首个失败成员的真实诊断，供同批其他成员保留根因。 */
		const failureDiagnosticByBatchKey = new Map<string, ExecutionDiagnostic>();
		const orderedReferences = [...references].sort((left, right) => (
			Number(forcedReplacementReferenceIdSet.has(left.referenceId))
			- Number(forcedReplacementReferenceIdSet.has(right.referenceId))
		));
		// 缓存只活在本批次；查询结果和边/面/顶点索引一起复用，不逐边重复构造或跨 revision 保留。
		const topologyHosts = new Map<string, Promise<TopologyOwnerRuntimeHost>>();
		const exactOwners = new Set(references.flatMap(reference => {
			const source = reference.sourceDescriptor;
			return source.kind === 'topology-edge' && source.locator.resolutionLevel === 'exact' && source.locator.curveKind !== 'line'
				? [resolveTopologySourceMemberScopeKey(source.owner)] : [];
		}));
		const runtimeItems: RebuildReferenceRuntimeItem[] = [];
		const diagnostics: ExecutionDiagnostic[] = [];
		/**
		 * 阶段 B 必须让内部点位/约束写入继承 checkpoint 的传播身份，
		 * 否则上层会把同一 checkpoint 自己产生的 dirty 误判成外部并发修改。
		 */
		const definitionChangeOwnerId = propagationContext?.definitionChangeOwnerId
			?? this.createRebuildDefinitionChangeOwnerId();
		// baseline 必须在 batch 开始时记录；后续同批引用产生的 owner dirty 不应阻断下一条引用。
		const targetSketchBaselineDefinitionRevisions = batchStartState
			?.targetSketchBaselineDefinitionRevisions
			?? new Map<string, number>();
		const dirtyTargetSketchIdsAtBatchStart = batchStartState
			?.dirtyTargetSketchIdsAtBatchStart
			?? new Set<string>();
		references.forEach(reference => {
			if (targetSketchBaselineDefinitionRevisions.has(reference.targetSketchId)) {
				return;
			}
			const targetSketch = this.dependencies.runtime.getSketchGroup(reference.targetSketchId);
			if (!targetSketch) {
				return;
			}
			targetSketchBaselineDefinitionRevisions.set(reference.targetSketchId, targetSketch.definitionRevision);
			if (targetSketch.hasUncommittedDefinitionChanges()) {
				dirtyTargetSketchIdsAtBatchStart.add(reference.targetSketchId);
			}
		});

		for (const reference of orderedReferences) {
			const batchKey = batchKeyByReferenceId.get(reference.referenceId)
				?? reference.referenceId;
			if (failedBatchKeys.has(batchKey)) {
				continue;
			}
			if (!isInputCurrent()) {
				if (transaction) {
					throw STALE_SKETCH_ENTITY_REFERENCE_REBUILD;
				}
				return this.createEmptyRebuildResult();
			}
			let rebuildResult: RebuildSingleReferenceResult;
			try {
				const preferredTargetAnchorBindings = forcedReplacementReferenceIdSet.has(reference.referenceId)
					? this.targetAnchorBindingCollector.collect({
						items: runtimeItems
							.filter(item => item.targetChange.targetSketchId === reference.targetSketchId)
							.map(item => ({
								projectionResult: item.projectionResult,
								targetEntity: item.targetEntity,
							})),
						resolveSourceObject: descriptor => this.dependencies.sourceObjectResolver.resolve(descriptor),
					})
					: undefined;
				let topologySource: SketchEntityReferenceRebuildExecutorInput['topologySource'];
				const source = reference.sourceDescriptor;
				const query = this.dependencies.runtime.queryTopologyFaceBoundaries;
				if (source.kind === 'topology-edge' && exactOwners.has(resolveTopologySourceMemberScopeKey(source.owner)) && query) {
					const sceneHost = this.dependencies.runtime.resolveTopologyOwnerHost?.(source);
					const sceneEdge = sceneHost?.getEdgeByIndex(source.locator.edgeIndex);
					// checkpoint 已携带精确几何，无需再次向 live Worker 查询。
					if (sceneHost?.listFaces && sceneEdge?.geometrySource !== 'brep') {
						const ownerKey = resolveTopologySourceMemberScopeKey(source.owner);
						let pending = topologyHosts.get(ownerKey);
						if (!pending) {
							pending = query.call(this.dependencies.runtime, {
								owner: source.owner,
								faceIndices: sceneHost.listFaces().map(face => face.faceIndex),
							}).then(queried => {
								// 旧边索引可能已失效；坐标变换属于已确认的结果成员，不属于那条旧边。
								const memberEdge = sceneHost.listEdges().find(edge => edge.topologyMemberId === queried.resolvedResultObjectId);
								return this.topologyHostFactory.create({
									topology: queried.topology,
									topologyMemberId: queried.resolvedResultObjectId,
									transformPoint: (memberEdge ?? sceneEdge)?.toWorldPoint,
								});
							});
							topologyHosts.set(ownerKey, pending);
						}
						const host = await pending;
						if (!isInputCurrent()) throw STALE_SKETCH_ENTITY_REFERENCE_REBUILD;
						const relocated = this.topologyRelocationService.relocate({descriptor: source, host});
						if (relocated.status === 'failed') throw new Error(relocated.diagnostic.message);
						topologySource = {
							descriptor: this.topologyDescriptorFactory.create({owner: source.owner, edge: relocated.edge, originSelectionIntentId: source.originSelectionIntentId}),
							runtimeSource: this.topologySnapshotFactory.create(relocated.edge),
						};
					}
				}
				rebuildResult = await this.rebuildExecutor.execute({
					reference,
					topologySource,
					repository,
					transaction,
					batchKey,
					forceTargetReplacement: forcedReplacementReferenceIdSet.has(
						reference.referenceId,
					),
					...(preferredTargetAnchorBindings ? {preferredTargetAnchorBindings} : {}),
					definitionChangeOwnerId,
					targetSketchBaselineDefinitionRevisions,
					dirtyTargetSketchIdsAtBatchStart,
					sourceMissingPolicy,
					isInputCurrent,
				});
				if (!isInputCurrent()) {
					if (transaction) {
						throw STALE_SKETCH_ENTITY_REFERENCE_REBUILD;
					}
					return this.createEmptyRebuildResult();
				}
			} catch (error) {
				if (error === STALE_SKETCH_ENTITY_REFERENCE_REBUILD) {
					throw error;
				}
				if (!isInputCurrent()) {
					if (transaction) {
						throw STALE_SKETCH_ENTITY_REFERENCE_REBUILD;
					}
					return this.createEmptyRebuildResult();
				}
				if (transaction) {
					diagnostics.push(...await this.rollbackReferenceBatch(
						transaction,
						batchPlanByBatchKey.get(batchKey),
						batchKey,
					));
				}
				failedBatchKeys.add(batchKey);
				// 单条执行器即使在失败状态持久化时再次抛错，也不能阻断同批次的其他引用。
				failedReferenceIds.push(reference.referenceId);
				const failureDiagnostic = createExecutionDiagnostic({
					kind: 'rebuild-failed',
					level: 'error',
					message: `单条引用重建异常，已隔离并继续处理剩余批次: ${error instanceof Error ? error.message : String(error)}`,
					referenceId: reference.referenceId,
					sourceRefEntityId: resolveSourceRefEntityId(reference.sourceDescriptor),
					targetEntityId: reference.targetEntityId,
				});
				failureDiagnosticByBatchKey.set(batchKey, failureDiagnostic);
				diagnostics.push(failureDiagnostic);
				await this.markReferenceFailedSafely({
					referenceId: reference.referenceId,
					sourceRefEntityId: resolveSourceRefEntityId(reference.sourceDescriptor),
					targetEntityId: reference.targetEntityId,
					diagnostics,
					diagnostic: failureDiagnostic,
					context: '隔离单条重建异常',
					isInputCurrent,
					repository,
				});
				continue;
			}
			diagnostics.push(...rebuildResult.diagnostics);
			if (rebuildResult.status === 'kept') {
				keptReferenceIds.push(reference.referenceId);
				if (rebuildResult.runtimeItem) {
					runtimeItems.push(rebuildResult.runtimeItem);
				}
				continue;
			}
			if (rebuildResult.status === 'removed') {
				removedReferenceIds.push(reference.referenceId);
				continue;
			}
			if (transaction) {
				diagnostics.push(...await this.rollbackReferenceBatch(
					transaction,
					batchPlanByBatchKey.get(batchKey),
					batchKey,
				));
			}
			failedBatchKeys.add(batchKey);
			const failureDiagnostic = [...rebuildResult.diagnostics]
				.reverse()
				.find(item => item.level === 'error');
			if (failureDiagnostic) {
				failureDiagnosticByBatchKey.set(batchKey, failureDiagnostic);
			}
			await this.markReferenceFailedSafely({
				referenceId: reference.referenceId,
				sourceRefEntityId: resolveSourceRefEntityId(reference.sourceDescriptor),
				targetEntityId: reference.targetEntityId,
				diagnostics,
				diagnostic: failureDiagnostic,
				context: '保存单条重建失败诊断',
				isInputCurrent,
				repository,
			});
			failedReferenceIds.push(reference.referenceId);
		}

		for (const failedBatchKey of failedBatchKeys) {
			const batchPlan = batchPlans.find(plan => plan.batchKey === failedBatchKey);
			if (!batchPlan) {
				continue;
			}
			const rootFailureDiagnostic = failureDiagnosticByBatchKey.get(failedBatchKey);
			for (const referenceId of batchPlan.referenceIds) {
				const alreadyFailed = failedReferenceIds.includes(referenceId);
				if (!alreadyFailed) {
					failedReferenceIds.push(referenceId);
				}
				if (transaction && !alreadyFailed) {
					const reference = references.find(item => item.referenceId === referenceId);
					await repository.updateReferenceStatus(
						referenceId,
						'failed',
						createExecutionDiagnostic({
							kind: 'rebuild-failed',
							level: 'error',
							message: `原子引用批次失败，已恢复上一次有效投影: ${failedBatchKey}${
								rootFailureDiagnostic
									? `；首错：${rootFailureDiagnostic.message}`
									: ''
							}`,
							referenceId,
							targetSketchId: reference?.targetSketchId,
							targetEntityId: reference?.targetEntityId,
						}),
					);
				}
			}
			for (let index = keptReferenceIds.length - 1; index >= 0; index -= 1) {
				if (batchPlan.referenceIds.includes(keptReferenceIds[index]!)) {
					keptReferenceIds.splice(index, 1);
				}
			}
			for (let index = runtimeItems.length - 1; index >= 0; index -= 1) {
				if (batchPlan.referenceIds.includes(runtimeItems[index]!.referenceId)) {
					runtimeItems.splice(index, 1);
				}
			}
		}

		const constraintRestoreFailedReferenceIds: string[] = [];
		if (keptReferenceIds.length > 0) {
			if (!isInputCurrent()) {
				if (transaction) {
					throw STALE_SKETCH_ENTITY_REFERENCE_REBUILD;
				}
				return this.createEmptyRebuildResult();
			}
			try {
				if (!transaction) {
					throw new Error('转换实体引用重建缺少统一运行时补偿事务');
				}
				const restoreResult = await this.restoreBatchBasicConstraints({
					runtimeItems,
					definitionChangeOwnerId,
					batchKeyByReferenceId,
					batchPlanByBatchKey,
					transaction,
				});
				diagnostics.push(...restoreResult.diagnostics);
				constraintRestoreFailedReferenceIds.push(...restoreResult.failedReferenceIds);
			} catch (error) {
				diagnostics.push(
					createExecutionDiagnostic({
						kind: 'restore-failed',
						level: 'warning',
						message: `批次重建后恢复基础重合失败: ${error instanceof Error ? error.message : String(error)}`,
					})
				);
			}
		}

		/**
		 * 基础关系恢复异常的引用不进入本轮 target-rebuilt 提交。
		 * 后续统一参数求解未通过只返回 warning，不得覆盖转换实体自身的成功状态。
		 */
		for (const referenceId of constraintRestoreFailedReferenceIds) {
			const runtimeItem = runtimeItems.find(candidate => candidate.referenceId === referenceId);
			if (!runtimeItem) {
				continue;
			}
			await this.markReferenceFailedSafely({
				referenceId,
				sourceRefEntityId: resolveSourceRefEntityId(runtimeItem.projectionResult.sourceDescriptor),
				targetEntityId: runtimeItem.targetChange.newTargetEntityId,
				diagnostics,
				diagnostic: [...diagnostics].reverse().find(item => (
					item.kind === 'restore-failed'
					&& item.level === 'error'
					&& item.referenceId === referenceId
				)),
				context: '基础关系恢复异常',
				isInputCurrent,
				repository,
			});
		}
		const constraintRestoreFailedReferenceIdSet = new Set(constraintRestoreFailedReferenceIds);
		const eligibleRuntimeItems = runtimeItems.filter(
			runtimeItem => !constraintRestoreFailedReferenceIdSet.has(runtimeItem.referenceId),
		);

		if (!isInputCurrent()) {
			if (transaction) {
				throw STALE_SKETCH_ENTITY_REFERENCE_REBUILD;
			}
			return this.createEmptyRebuildResult();
		}
		const targetChangesForCommit = [
			...additionalTargetChanges.filter(
				targetChange => !constraintRestoreFailedReferenceIdSet.has(targetChange.referenceId),
			),
			...eligibleRuntimeItems.map(runtimeItem => runtimeItem.targetChange),
		];
		const targetChangeCommitInput = {
			changes: targetChangesForCommit,
			runtime: this.dependencies.runtime,
			allowDirtyTargetSketches: true,
			propagationId: propagationContext?.propagationId,
			visitedReferenceIds: propagationContext?.visitedReferenceIds,
			allowedDefinitionChangeOwnerId: definitionChangeOwnerId,
			targetSketchBaselineDefinitionRevisions,
			isInputCurrent,
		};
		if (!transaction) {
			throw new Error('转换实体引用目标提交缺少统一运行时补偿事务');
		}
		const targetChangePreparation = this.targetChangeCommitter.prepare(
			targetChangeCommitInput,
		);
		diagnostics.push(...targetChangePreparation.diagnostics);
		const rejectedTargetSketchIdSet = new Set(
			targetChangePreparation.rejectedSketchIds,
		);
		for (const rejectedTargetSketchId of rejectedTargetSketchIdSet) {
			diagnostics.push(...await transaction.rollbackTargetSketch(
				rejectedTargetSketchId,
			));
		}
		if (targetChangePreparation.sketchCommits.length > 0) {
			transaction.record({
				targetSketchId: targetChangePreparation.preparedSketchIds[0]
					?? 'unknown-sketch',
				batchKey: `${definitionChangeOwnerId}::target-change-commit`,
				description: '发布转换实体引用目标变化',
				finalizePhase: 'publish',
				rollback: () => undefined,
				finalize: () => this.targetChangeCommitter
					.commitPrepared(targetChangePreparation)
					.diagnostics,
			});
		}

		const commitFailedReferenceIds = await this.markUncommittedTargetChangesAsFailed({
			runtimeItems: eligibleRuntimeItems,
			targetChanges: targetChangesForCommit,
			committedSketchIds: [...targetChangePreparation.preparedSketchIds],
			diagnostics,
			isInputCurrent,
			repository,
		});
		if (!isInputCurrent()) {
			if (transaction) {
				throw STALE_SKETCH_ENTITY_REFERENCE_REBUILD;
			}
			return this.createEmptyRebuildResult();
		}
		if (commitFailedReferenceIds.length > 0 || constraintRestoreFailedReferenceIds.length > 0) {
			const unsuccessfulReferenceIdSet = new Set([
				...commitFailedReferenceIds,
				...constraintRestoreFailedReferenceIds,
			]);
			return {
				keptReferenceIds: keptReferenceIds.filter(referenceId => !unsuccessfulReferenceIdSet.has(referenceId)),
				removedReferenceIds,
				failedReferenceIds: [...failedReferenceIds, ...unsuccessfulReferenceIdSet],
				diagnostics,
			};
		}

		return {
			keptReferenceIds,
			removedReferenceIds,
			failedReferenceIds,
			diagnostics,
		};
	}

	/**
	 * 将未成功发布 target-rebuilt commit 的引用标记为 failed。
	 * 说明：
	 * 1. 下游传播依赖 `reference-target-rebuilt` commit 继续前进；
	 * 2. 若某个目标草图没有完成提交，继续把引用返回为 kept 会形成静默断链；
	 * 3. 因此这里把这类引用显式降级为 failed，让调用方能感知事务没有闭合。
	 *
	 * @param input 提交收口输入。
	 * @returns 本轮因提交失败而降级的引用 id。
	 */
	private async markUncommittedTargetChangesAsFailed(input: {
		runtimeItems: RebuildReferenceRuntimeItem[];
		targetChanges: SketchEntityReferenceTargetChange[];
		committedSketchIds: string[];
		diagnostics: ExecutionDiagnostic[];
		isInputCurrent: () => boolean;
		repository: SketchEntityReferenceRepositoryWriteScope;
	}): Promise<string[]> {
		if (
			(input.runtimeItems.length <= 0 && input.targetChanges.length <= 0)
			|| !input.isInputCurrent()
		) {
			return [];
		}

		const committedSketchIdSet = new Set(input.committedSketchIds);
		const failedRuntimeItems = input.runtimeItems.filter(runtimeItem => !committedSketchIdSet.has(runtimeItem.targetChange.targetSketchId));
		const failedRuntimeItemByReferenceId = new Map(failedRuntimeItems.map(runtimeItem => [runtimeItem.referenceId, runtimeItem]));
		const failedTargetChangeByReferenceId = new Map(
			input.targetChanges
				.filter(change => !committedSketchIdSet.has(change.targetSketchId))
				.map(change => [change.referenceId, change]),
		);
		const failedReferenceIds = [...new Set([
			...failedRuntimeItemByReferenceId.keys(),
			...failedTargetChangeByReferenceId.keys(),
		])];
		for (const referenceId of failedReferenceIds) {
			if (!input.isInputCurrent()) {
				return [];
			}
			const runtimeItem = failedRuntimeItemByReferenceId.get(referenceId);
			const targetChange = runtimeItem?.targetChange
				?? failedTargetChangeByReferenceId.get(referenceId);
			if (!targetChange) {
				continue;
			}
			const existingDiagnostic = [...input.diagnostics].reverse().find(item => (
				item.level === 'error'
					&& (
						item.referenceId === referenceId
							|| (
								item.referenceId === undefined
								&& item.targetSketchId === targetChange.targetSketchId
							)
						)
			));
			const failureDiagnostic = existingDiagnostic ?? createExecutionDiagnostic({
				kind: 'rebuild-failed',
				level: 'error',
				message: `引用目标重建提交未完成，已阻断下游传播: ${referenceId}`,
				referenceId,
				sourceRefEntityId: runtimeItem
					? resolveSourceRefEntityId(runtimeItem.projectionResult.sourceDescriptor)
					: undefined,
				targetEntityId: targetChange.newTargetEntityId,
			});
			if (!existingDiagnostic) {
				input.diagnostics.push(failureDiagnostic);
			}
			await this.markReferenceFailedSafely({
				referenceId,
				sourceRefEntityId: runtimeItem
					? resolveSourceRefEntityId(runtimeItem.projectionResult.sourceDescriptor)
					: undefined,
				targetEntityId: targetChange.newTargetEntityId,
				diagnostics: input.diagnostics,
				diagnostic: failureDiagnostic,
				context: '收口未提交的目标变化',
				isInputCurrent: input.isInputCurrent,
				repository: input.repository,
			});
		}

		return failedReferenceIds;
	}

	/**
	 * 回滚最终批次键及协调阶段提前登记的全部语义键。
	 *
	 * @param transaction 当前运行时补偿事务。
	 * @param batchPlan 最终原子批次计划。
	 * @param fallbackBatchKey 缺少计划时使用的单条批次键。
	 * @returns 回滚过程中产生的补偿诊断。
	 */
	private rollbackReferenceBatch(
		transaction: SketchEntityReferenceRebuildBatchTransaction,
		batchPlan: SketchEntityReferenceRebuildBatchPlan | undefined,
		fallbackBatchKey: string,
	): Promise<ExecutionDiagnostic[]> {
		return transaction.rollbackBatchKeys([
			batchPlan?.batchKey ?? fallbackBatchKey,
			...(batchPlan?.rollbackKeys ?? []),
		]);
	}

	/**
	 * 尽力把单条引用持久化为 failed，而不让状态写入故障中断整个批次。
	 *
	 * @param input 状态写入上下文及诊断集合。
	 */
	private async markReferenceFailedSafely(input: {
		referenceId: string;
		sourceRefEntityId?: string;
		targetEntityId?: string;
		diagnostics: ExecutionDiagnostic[];
		context: string;
		isInputCurrent?: () => boolean;
		repository: SketchEntityReferenceRepositoryWriteScope;
		diagnostic?: ExecutionDiagnostic;
	}): Promise<void> {
		if (input.isInputCurrent && !input.isInputCurrent()) {
			return;
		}
		try {
			if (input.diagnostic) {
				await input.repository.updateReferenceStatus(
					input.referenceId,
					'failed',
					input.diagnostic,
				);
			} else {
				await input.repository.updateReferenceStatus(input.referenceId, 'failed');
			}
		} catch (error) {
			input.diagnostics.push(
				createExecutionDiagnostic({
					kind: 'rebuild-failed',
					level: 'error',
					message: `${input.context}时写入 failed 状态失败: ${error instanceof Error ? error.message : String(error)}`,
					referenceId: input.referenceId,
					sourceRefEntityId: input.sourceRefEntityId,
					targetEntityId: input.targetEntityId,
				})
			);
		}
	}

	/**
	 * 创建没有提交任何引用变化的空结果。
	 *
	 * @returns 空重建结果。
	 */
	private createEmptyRebuildResult(): RebuildSketchEntityReferencesResult {
		return {
			keptReferenceIds: [],
			removedReferenceIds: [],
			failedReferenceIds: [],
			diagnostics: [],
		};
	}

	/**
	 * 在同一 source sketch 的 rebuild 批次完成后，恢复一次目标端点的基础重合拓扑。
	 * 说明：
	 * 1. create 阶段会恢复“同批次共享源端点 -> 目标端点重合”；
	 * 2. rebuild 阶段若不重复进入该步骤，same-kind 的静默点位更新不会把共享端点再次收紧；
	 * 3. kind-changed 路径生成的新目标点也需要在这里重新接回批次拓扑。
	 *
	 * @param input 批次恢复输入。
	 * @returns 恢复阶段诊断。
	 */
	private async restoreBatchBasicConstraints(input: {
		runtimeItems: RebuildReferenceRuntimeItem[];
		definitionChangeOwnerId: string;
		batchKeyByReferenceId: ReadonlyMap<string, string>;
		batchPlanByBatchKey: ReadonlyMap<string, SketchEntityReferenceRebuildBatchPlan>;
		transaction: SketchEntityReferenceRebuildBatchTransaction;
	}) {
		if (input.runtimeItems.length <= 0) {
			return {
				diagnostics: [] as ExecutionDiagnostic[],
				failedReferenceIds: [] as string[],
			};
		}

		/**
		 * 端点拓扑和基础关系属于转换实体原子批次，不能先按目标草图合并。
		 * 统一参数求解仍在全部成功批次准备完成后按目标草图执行一次。
		 */
		const runtimeItemsByBatchKey = new Map<string, RebuildReferenceRuntimeItem[]>();
		input.runtimeItems.forEach(runtimeItem => {
			const batchKey = input.batchKeyByReferenceId.get(runtimeItem.referenceId)
				?? runtimeItem.referenceId;
			const batchItems = runtimeItemsByBatchKey.get(batchKey) ?? [];
			batchItems.push(runtimeItem);
			runtimeItemsByBatchKey.set(batchKey, batchItems);
		});

		const diagnostics: ExecutionDiagnostic[] = [];
		const failedReferenceIds: string[] = [];
		const preparedBatchesByTargetSketchId = new Map<string, Array<{
			batchKey: string;
			runtimeItems: RebuildReferenceRuntimeItem[];
			endpointTransaction: SketchEntityReferenceRebuildBatchTransaction;
		}>>();

		for (const [batchKey, batchItems] of runtimeItemsByBatchKey) {
			let endpointTransaction: SketchEntityReferenceRebuildBatchTransaction | null = null;
			try {
				const targetAnchorBindings = this.targetAnchorBindingCollector.collect({
					items: batchItems.map(runtimeItem => ({
						projectionResult: runtimeItem.projectionResult,
						targetEntity: runtimeItem.targetEntity,
					})),
					resolveSourceObject: descriptor => this.dependencies.sourceObjectResolver.resolve(descriptor),
				});
				if (targetAnchorBindings.size <= 0) {
					continue;
				}
				const targetSketchId = batchItems[0]?.targetChange.targetSketchId;
				const targetSketch = targetSketchId
					? this.dependencies.runtime.getSketchGroup(targetSketchId)
					: null;
				if (!targetSketch) {
					throw new Error(`转换实体引用批次重建缺少目标草图: ${targetSketchId ?? 'unknown'}`);
				}
				const batchDiagnostics = this.runTargetSketchDefinitionChangeOwnerScopes({
					runtimeItems: batchItems,
					definitionChangeOwnerId: input.definitionChangeOwnerId,
					work: () => {
						endpointTransaction = new SketchEntityReferenceRebuildBatchTransaction({
							targetSketch,
							targetAnchorBindings,
							targetEndpointCanonicalizer: this.targetEndpointCanonicalizer,
						});
						try {
							endpointTransaction.applyEndpointCanonicalization(
								this.collectPreferredTargetPointUuids(batchItems),
							);
							const restoreResult = endpointTransaction.captureRestoredRelations(() => (
								this.basicConstraintRestoreService.restore({
									sourceDescriptors: batchItems.map(runtimeItem => runtimeItem.projectionResult.sourceDescriptor),
									runtime: this.dependencies.runtime,
									targetAnchorBindings,
								})
							));
							return restoreResult.diagnostics;
						} catch (error) {
							endpointTransaction.rollback();
							throw error;
						}
					},
				});
				diagnostics.push(...batchDiagnostics);
				if (!endpointTransaction || !targetSketchId) {
					throw new Error(`转换实体引用批次缺少端点补偿事务: ${batchKey}`);
				}
				const preparedEndpointTransaction = endpointTransaction;
				input.transaction.record({
					targetSketchId,
					batchKey,
					description: '提交转换实体引用端点拓扑',
					finalizePhase: 'prepare',
					rollback: () => preparedEndpointTransaction.rollback(),
					finalize: () => preparedEndpointTransaction.commit(),
				});
				const preparedBatches = preparedBatchesByTargetSketchId.get(targetSketchId) ?? [];
				preparedBatches.push({
					batchKey,
					runtimeItems: batchItems,
					endpointTransaction: preparedEndpointTransaction,
				});
				preparedBatchesByTargetSketchId.set(targetSketchId, preparedBatches);
			} catch (error) {
				diagnostics.push(...await this.rollbackReferenceBatch(
					input.transaction,
					input.batchPlanByBatchKey.get(batchKey),
					batchKey,
				));
				failedReferenceIds.push(...batchItems.map(item => item.referenceId));
				diagnostics.push(...batchItems.map(item => createExecutionDiagnostic({
					kind: 'restore-failed',
					level: 'error',
					message: `原子引用批次候选恢复失败: ${error instanceof Error ? error.message : String(error)}`,
					referenceId: item.referenceId,
					targetEntityId: item.targetChange.newTargetEntityId,
				})));
			}
		}

		for (const [targetSketchId, preparedBatches] of preparedBatchesByTargetSketchId) {
			const targetSketchItems = preparedBatches.flatMap(batch => batch.runtimeItems);
			const solved = this.runTargetSketchDefinitionChangeOwnerScopes({
				runtimeItems: targetSketchItems,
				definitionChangeOwnerId: input.definitionChangeOwnerId,
				work: () => this.dependencies.solveTargetSketchParametricState(targetSketchId),
			});
			if (solved) {
				continue;
			}
			diagnostics.push(createExecutionDiagnostic({
				kind: 'relation-solve-failed',
				level: 'warning',
				message: `转换实体已更新，但后续统一参数求解未通过: ${targetSketchId}`,
				referenceId: targetSketchItems[0]?.referenceId,
				targetEntityId: targetSketchItems[0]?.targetChange.newTargetEntityId,
			}));
		}

		return {
			diagnostics,
			failedReferenceIds: [...new Set(failedReferenceIds)],
		};
	}

	/**
	 * 收集仍由 in-place 目标实体持有的稳定点，目标类型替换时优先复用这些点。
	 *
	 * @param runtimeItems 当前目标草图的重建运行时项。
	 * @returns 应优先保留的目标点 UUID。
	 */
	private collectPreferredTargetPointUuids(
		runtimeItems: readonly RebuildReferenceRuntimeItem[],
	): ReadonlySet<string> {
		const preferredPointUuids = new Set<string>();
		runtimeItems.forEach((runtimeItem) => {
			if (runtimeItem.targetChange.changeKind !== 'in-place') {
				return;
			}
			if (!isAbstractSketchEdgeObject(runtimeItem.targetEntity)) {
				return;
			}
			const relatedPoints: readonly SketchPointObject[] =
				runtimeItem.targetEntity.getRelatedSketchPointsSnapshot();
			relatedPoints.forEach(point => preferredPointUuids.add(point.uuid));
		});
		return preferredPointUuids;
	}

	/**
	 * 在本批目标草图的定义变更 owner 作用域内执行逻辑。
	 *
	 * @param input owner 作用域输入。
	 * @returns work 的返回值。
	 */
	private runTargetSketchDefinitionChangeOwnerScopes<T>(input: {
		runtimeItems: RebuildReferenceRuntimeItem[];
		definitionChangeOwnerId: string;
		work: () => T;
	}): T {
		const targetSketches: SketchGroup[] = [];
		const seenSketchIds = new Set<string>();
		input.runtimeItems.forEach(runtimeItem => {
			const targetSketchId = runtimeItem.targetChange.targetSketchId;
			if (seenSketchIds.has(targetSketchId)) {
				return;
			}
			const targetSketch = this.dependencies.runtime.getSketchGroup(targetSketchId);
			if (!targetSketch) {
				return;
			}
			seenSketchIds.add(targetSketchId);
			targetSketches.push(targetSketch);
		});

		const runAt = (index: number): T => {
			const targetSketch = targetSketches[index];
			if (!targetSketch) {
				return input.work();
			}
			return targetSketch.runDefinitionChangeOwnerScope(input.definitionChangeOwnerId, () => runAt(index + 1));
		};

		return runAt(0);
	}

	/**
	 * 冻结批次规划使用的来源端点和目标点身份。
	 *
	 * @param references 当前 checkpoint 引用记录。
	 * @returns 按引用 ID 索引的端点证据。
	 */
	private collectRebuildEndpointEvidence(
		references: readonly SketchEntityReferenceRecord[],
	): Map<string, {sourceAnchorIds: string[]; targetPointIds: string[]}> {
		const evidenceByReferenceId = new Map<
			string,
			{sourceAnchorIds: string[]; targetPointIds: string[]}
		>();
		references.forEach((reference) => {
			const sourceObject = this.dependencies.sourceObjectResolver.resolve(
				reference.sourceDescriptor,
			);
			const targetObject = this.dependencies.runtime.getSketchObject(
				reference.targetSketchId,
				reference.targetEntityId,
			);
			const recordedSourceAnchorIds = reference.rebuildPayload.endpointAnchorBindings
				?.map(binding => binding.sourceAnchorId)
				?? [];
			evidenceByReferenceId.set(reference.referenceId, {
				sourceAnchorIds: [...new Set([
					...recordedSourceAnchorIds,
					...this.collectRelatedPointIds(sourceObject),
				])].sort(),
				targetPointIds: this.collectRelatedPointIds(targetObject),
			});
		});
		return evidenceByReferenceId;
	}

	/**
	 * 读取草图对象正式关联点的运行时身份。
	 *
	 * @param object 草图来源或目标对象。
	 * @returns 去重后的点 UUID。
	 */
	private collectRelatedPointIds(object: Object3D | null): string[] {
		if (!isAbstractSketchEdgeObject(object)) {
			return [];
		}
		const points = object.getRelatedSketchPointsSnapshot();
		return [...new Set(points.map(point => point.uuid))].sort();
	}

	/**
	 * 创建本轮重建定义变更 owner。
	 *
	 * @returns owner id。
	 */
	private createRebuildDefinitionChangeOwnerId(): string {
		const cryptoApi = globalThis.crypto;
		if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
			return `reference-rebuild-${cryptoApi.randomUUID()}`;
		}

		return `reference-rebuild-${Date.now()}-${Math.random().toString(36).slice(2)}`;
	}
}
